import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ASPECT_RATIO_OPTIONS,
  clampCustomCm,
  clampSimulatedWidth,
  DEFAULT_ASPECT_RATIO_ID,
  DEFAULT_SIMULATED_WIDTH,
  estimateMonitorDiagonalInches,
  getTvSimulationMetrics,
  INCH_SIMULATION_OPTIONS,
  isCompleteCustomSize,
  RESOLUTION_SIMULATION_OPTIONS,
  type AspectRatioId,
  type InchSimulationOption,
  type ScreenConfig,
} from '../utils/inchSimulation';

export interface UseInchSimulationResult {
  nativeInches: number;
  selectedInches: number | null;
  selectedWidthPx: number | null;
  selectedAspectId: AspectRatioId;
  customWidthCm: number | null;
  customHeightCm: number | null;
  isCustomSizeActive: boolean;
  hasPartialCustomSize: boolean;
  isSimulating: boolean;
  simulatedScreen: ScreenConfig;
  canvasWidth: number;
  canvasHeight: number;
  cssWidth: number;
  cssHeight: number;
  inchScale: number;
  options: readonly InchSimulationOption[];
  resolutionOptions: typeof RESOLUTION_SIMULATION_OPTIONS;
  aspectOptions: typeof ASPECT_RATIO_OPTIONS;
  sliderWidth: number;
  controlsOpen: boolean;
  setSelectedInches: (inches: number | null) => void;
  setSelectedWidthPx: (widthPx: number | null) => void;
  setSelectedAspectId: (id: AspectRatioId) => void;
  setCustomWidthCm: (cm: number | null) => void;
  setCustomHeightCm: (cm: number | null) => void;
  setControlsOpen: (open: boolean) => void;
  resetToNative: () => void;
}

export function useInchSimulation(): UseInchSimulationResult {
  const [nativeInches, setNativeInches] = useState(() => estimateMonitorDiagonalInches());
  const [selectedInches, setSelectedInchesState] = useState<number | null>(null);
  const [selectedWidthPx, setSelectedWidthPxState] = useState<number | null>(null);
  const [selectedAspectId, setSelectedAspectIdState] = useState<AspectRatioId>(DEFAULT_ASPECT_RATIO_ID);
  const [customWidthCm, setCustomWidthCmState] = useState<number | null>(null);
  const [customHeightCm, setCustomHeightCmState] = useState<number | null>(null);
  const [controlsOpen, setControlsOpen] = useState(false);
  const [container, setContainer] = useState(() => ({
    width: typeof window !== 'undefined' ? window.innerWidth : 1920,
    height: typeof window !== 'undefined' ? window.innerHeight : 1080,
  }));

  useEffect(() => {
    const refresh = () => {
      setNativeInches(estimateMonitorDiagonalInches());
      setContainer({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', refresh);
    return () => window.removeEventListener('resize', refresh);
  }, []);

  const setSelectedInches = useCallback((inches: number | null) => {
    if (inches == null) {
      setSelectedInchesState(null);
      return;
    }
    setCustomWidthCmState(null);
    setCustomHeightCmState(null);
    setSelectedInchesState(Math.min(50, Math.max(20, Math.round(inches))));
    setSelectedWidthPxState((current) => current ?? DEFAULT_SIMULATED_WIDTH);
  }, []);

  const setSelectedAspectId = useCallback((id: AspectRatioId) => {
    setSelectedAspectIdState(id);
    if (id !== DEFAULT_ASPECT_RATIO_ID) {
      setCustomWidthCmState(null);
      setCustomHeightCmState(null);
      setSelectedWidthPxState((current) => current ?? DEFAULT_SIMULATED_WIDTH);
    }
  }, []);

  const setSelectedWidthPx = useCallback((widthPx: number | null) => {
    if (widthPx == null) {
      setSelectedWidthPxState(null);
      return;
    }
    setSelectedWidthPxState(clampSimulatedWidth(widthPx));
  }, []);

  const setCustomWidthCm = useCallback((cm: number | null) => {
    if (cm == null) {
      setCustomWidthCmState(null);
      return;
    }
    setSelectedInchesState(null);
    setCustomWidthCmState(clampCustomCm(cm));
  }, []);

  const setCustomHeightCm = useCallback((cm: number | null) => {
    if (cm == null) {
      setCustomHeightCmState(null);
      return;
    }
    setSelectedInchesState(null);
    setCustomHeightCmState(clampCustomCm(cm));
  }, []);

  const resetToNative = useCallback(() => {
    setSelectedInchesState(null);
    setSelectedWidthPxState(null);
    setCustomWidthCmState(null);
    setCustomHeightCmState(null);
    setSelectedAspectIdState(DEFAULT_ASPECT_RATIO_ID);
  }, []);

  const isCustomSizeActive = isCompleteCustomSize(customWidthCm, customHeightCm);
  const hasPartialCustomSize = customWidthCm != null || customHeightCm != null;
  const isSimulating =
    selectedInches != null ||
    selectedWidthPx != null ||
    isCustomSizeActive ||
    selectedAspectId !== DEFAULT_ASPECT_RATIO_ID;

  const metrics = useMemo(
    () =>
      getTvSimulationMetrics({
        selectedInches,
        selectedWidthPx,
        selectedAspectId,
        customWidthCm,
        customHeightCm,
        containerWidth: container.width,
        containerHeight: container.height,
      }),
    [
      selectedInches,
      selectedWidthPx,
      selectedAspectId,
      customWidthCm,
      customHeightCm,
      container.width,
      container.height,
    ]
  );

  return {
    nativeInches,
    selectedInches,
    selectedWidthPx,
    selectedAspectId,
    customWidthCm,
    customHeightCm,
    isCustomSizeActive,
    hasPartialCustomSize,
    isSimulating,
    simulatedScreen: metrics.simulated,
    canvasWidth: metrics.cssWidth,
    canvasHeight: metrics.cssHeight,
    cssWidth: metrics.cssWidth,
    cssHeight: metrics.cssHeight,
    inchScale: metrics.scale,
    options: INCH_SIMULATION_OPTIONS,
    resolutionOptions: RESOLUTION_SIMULATION_OPTIONS,
    aspectOptions: ASPECT_RATIO_OPTIONS,
    sliderWidth: selectedWidthPx ?? DEFAULT_SIMULATED_WIDTH,
    controlsOpen,
    setSelectedInches,
    setSelectedWidthPx,
    setSelectedAspectId,
    setCustomWidthCm,
    setCustomHeightCm,
    setControlsOpen,
    resetToNative,
  };
}
